import React, { useState, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';

// A number input that behaves naturally: you can fully CLEAR the field (no
// "stuck 0"), type decimals ("0.5", "15.") and even use a comma ("15,90"), and
// it never fights your cursor. It keeps its own string while you edit and emits
// a parsed number | emptyValue via onChange. When `decimals` is set (e.g. 2 for
// money) the value is tidied to that many decimals on blur — so "15.9" shows as
// "15.90". `emptyValue` is what an empty field means (null for an optional
// price, 0 for a required amount).
export function PriceInput({ value, onChange, emptyValue = null, decimals = null, ...props }) {
  const money = decimals != null;
  const editing = useRef(false);

  const display = (v) => {
    if (v == null || v === '') return '';
    if (money) { const n = Number(v); return Number.isNaN(n) ? '' : n.toFixed(decimals); }
    return String(v);
  };

  const [str, setStr] = useState(display(value));

  useEffect(() => {
    if (!editing.current) setStr(display(value));
  }, [value]);  // eslint-disable-line react-hooks/exhaustive-deps

  const parse = (raw) => {
    const cleaned = String(raw).replace(',', '.').trim();
    if (cleaned === '' || cleaned === '-' || cleaned === '.' || cleaned === '-.') return emptyValue;
    const n = parseFloat(cleaned);
    return Number.isNaN(n) ? emptyValue : n;
  };

  const handleChange = (e) => {
    let raw = e.target.value;
    if (money) raw = raw.replace(/[^0-9.,-]/g, '');  // keep text input numeric
    setStr(raw);
    onChange(parse(raw));
  };

  const handleFocus = (e) => {
    editing.current = true;
    setStr(value == null ? '' : String(value));  // edit the raw value, no trailing zeros
    props.onFocus?.(e);
  };

  const handleBlur = (e) => {
    editing.current = false;
    const n = parse(e.target.value);
    setStr(n == null ? '' : display(n));
    onChange(n);
    props.onBlur?.(e);
  };

  return (
    <Input
      type={money ? 'text' : 'number'}
      inputMode="decimal"
      value={str}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onChange={handleChange}
      {...props}
    />
  );
}
